import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import StatusBadge from "@/components/StatusBadge";
import { ArrowRight, Globe } from "iconoir-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicScore() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/share/${slug}`)
      .then((r) => setData(r.data))
      .catch(() => setError("This share link is no longer available."));
  }, [slug]);

  // Dynamic OG meta tags — helps Slack/Discord which run JS
  useEffect(() => {
    if (!data) return;
    document.title = `${data.business_name || data.website_url} · AI Visibility Score ${data.final_score}/100`;
    const meta = (name, content, prop) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        if (prop) el.setAttribute("property", name); else el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    const desc = data.summary || `AI Visibility Score: ${data.final_score}/100 (${data.status_label})`;
    const img = `${API}/og/score/${slug}.png`;
    meta("description", desc);
    meta("og:title", document.title, true);
    meta("og:description", desc, true);
    meta("og:image", img, true);
    meta("og:type", "website", true);
    meta("twitter:card", "summary_large_image");
    meta("twitter:title", document.title);
    meta("twitter:description", desc);
    meta("twitter:image", img);
  }, [data, slug]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-8" data-testid="share-not-found">
        <div className="text-center max-w-md">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">// 404</div>
          <h1 className="mt-4 font-marketing text-4xl">{error}</h1>
          <Link to="/" className="inline-block mt-6 px-5 py-3 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold hover:bg-intl transition-colors">
            Visit AI Visibility OS
          </Link>
        </div>
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-screen flex items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="min-h-screen bg-background grain" data-testid="public-score-page">
      {/* Top bar */}
      <header className="border-b border-border bg-bone/95 backdrop-blur-md sticky top-0 z-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="share-brand-link">
            <div className="w-3 h-3 bg-intl" />
            <span className="font-app font-bold">AI Visibility OS</span>
          </Link>
          <Link to="/register" data-testid="share-register-cta" className="px-4 py-2 bg-obsidian text-bone font-app text-xs uppercase tracking-widest font-bold hover:bg-intl transition-colors">
            Get your score →
          </Link>
        </div>
      </header>

      <main className="max-w-[1200px] mx-auto px-6 lg:px-12 py-16 lg:py-24">
        <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-3">
          <span className="w-8 h-px bg-intl" /> Public AI Visibility audit
        </div>

        <div className="mt-6 grid grid-cols-12 gap-6 lg:gap-8 items-start">
          <div className="col-span-12 lg:col-span-7">
            <div className="font-marketing text-2xl text-muted-foreground">{data.business_name || data.website_url}</div>
            <h1 className="mt-2 font-marketing text-6xl sm:text-7xl tracking-tighter leading-none">
              {data.website_url.replace(/^https?:\/\//, "")}<br />
              <span className="text-intl">is {data.status_label}</span> to AI search.
            </h1>
            {data.summary && <p className="mt-8 text-lg leading-relaxed text-muted-foreground max-w-2xl">{data.summary}</p>}
          </div>

          <div className="col-span-12 lg:col-span-5 border border-border bg-card p-8" data-testid="public-score-tile">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">AI Visibility Score</div>
            <div className="mt-4 flex items-end gap-3">
              <div className="font-mono text-7xl leading-none">{data.final_score}</div>
              <div className="font-mono text-xl text-muted-foreground mb-2">/100</div>
            </div>
            <div className="mt-4"><StatusBadge label={data.status_label} large /></div>
          </div>
        </div>

        {/* Category bars */}
        {data.categories?.length > 0 && (
          <div className="mt-16">
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">/ category breakdown</div>
            <h2 className="font-marketing text-3xl mt-1">Where the score comes from</h2>
            <div className="mt-6 border border-border bg-card divide-y divide-border" data-testid="public-categories">
              {data.categories.map((c) => (
                <div key={c.key} className="grid grid-cols-12 items-center gap-4 px-6 py-4">
                  <div className="col-span-12 md:col-span-4 font-app">{c.name}</div>
                  <div className="col-span-8 md:col-span-6 h-2 bg-muted">
                    <div className="h-full bg-intl" style={{ width: `${c.score}%` }} />
                  </div>
                  <div className="col-span-4 md:col-span-2 font-mono text-sm text-right">{c.score}/100</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-24 border-t border-border pt-16">
          <div className="grid grid-cols-12 gap-6 items-end">
            <h2 className="col-span-12 lg:col-span-8 font-marketing text-4xl sm:text-5xl tracking-tighter leading-none">
              Is <em>your</em> business ready for <span className="text-intl">AI Search</span>?
            </h2>
            <div className="col-span-12 lg:col-span-4 lg:text-right">
              <Link to="/register" data-testid="share-bottom-cta" className="inline-flex items-center gap-2 px-6 py-4 bg-obsidian text-bone font-app font-bold uppercase tracking-wider text-sm hover:bg-intl transition-colors">
                Run my free audit <ArrowRight />
              </Link>
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-border mt-16">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 py-6 flex justify-between items-center font-mono text-xs text-muted-foreground">
          <span>© 2026 AI Visibility OS</span>
          <Link to="/" className="hover:text-intl flex items-center gap-2">
            <Globe width={14} height={14} /> aivisibility.os
          </Link>
        </div>
      </footer>
    </div>
  );
}
